'use client';

import { Dialog as BaseDialog } from '@base-ui/react/dialog';
import type { ReactNode } from 'react';

import { cx } from '@/shared/lib/cx';

import styles from './dialog.module.scss';

export type DialogVariant = 'center' | 'right' | 'left';

export type DialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Accessible name. Rendered visually hidden unless showTitle is set. */
  title: string;
  showTitle?: boolean;
  /** The content renders its own visible <DialogTitle>; `title` is then not rendered. */
  ownTitle?: boolean;
  /** The portal leaves the page tree, so a themed page passes its theme to the popup. */
  theme?: 'editorial' | undefined;
  shadow?: 'dialog' | 'picker' | 'drawer-soft' | 'none';
  children: ReactNode;
  variant?: DialogVariant;
  /** Desktop width of the popup, e.g. "67.5rem". Mobile is always full width. */
  width?: string;
  backdrop?: 'default' | 'strong' | 'zoom' | 'soft' | 'picker';
  className?: string | undefined;
  /** Keep a centred dialog as a bottom sheet on phones instead of full screen. */
  mobileSheet?: boolean;
};

/** Modal dialog, drawer or sheet with focus trap, scroll lock and Escape dismissal (Base UI). */
export function Dialog({
  open,
  onOpenChange,
  title,
  showTitle = false,
  ownTitle = false,
  theme,
  shadow = 'dialog',
  children,
  variant = 'center',
  width,
  backdrop = 'default',
  className,
  mobileSheet = false,
}: DialogProps) {
  const style = width ? { '--dialog-width': width } : undefined;
  const themed = theme ? { 'data-theme': theme } : {};
  return (
    <BaseDialog.Root open={open} onOpenChange={(next) => onOpenChange(next)}>
      <BaseDialog.Portal>
        <BaseDialog.Backdrop className={cx(styles.backdrop)} data-tone={backdrop} />
        <BaseDialog.Popup
          className={cx(styles.popup, className)}
          data-variant={variant}
          data-shadow={shadow}
          data-mobile-sheet={mobileSheet || undefined}
          {...themed}
          {...(style ? { style } : {})}
        >
          {!ownTitle && (
            <BaseDialog.Title className={cx(showTitle ? styles.title : styles.hidden)}>
              {title}
            </BaseDialog.Title>
          )}
          {children}
        </BaseDialog.Popup>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  );
}

/** Visible dialog heading that names the dialog (use with `ownTitle`). */
export function DialogTitle({
  children,
  className,
  id,
}: {
  children: ReactNode;
  className?: string | undefined;
  id?: string;
}) {
  return (
    <BaseDialog.Title className={cx(className)} {...(id ? { id } : {})}>
      {children}
    </BaseDialog.Title>
  );
}
